import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ChatService } from '../../services/chat.service';
import { IRequestFile, IResponseFile } from '../../models/chat-message';
import { AlertPopupComponent } from '../common/alert-popup/alert-popup.component';
import {
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
  MAX_FILE_COUNT,
} from '../../utils/constants';
import { MatMenuModule } from '@angular/material/menu';
import heic2any from 'heic2any';

@Component({
  selector: 'app-file-upload',
  imports: [
    MatIcon,
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    AlertPopupComponent,
    MatMenuModule,
  ],
  standalone: true,
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit {
  @Input() configs: any = {};
  @Input() uploadedFiles: IResponseFile[] = [];
  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() cancelUpload = new EventEmitter<void>();
  @Output() action: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  showPreview = false;
  dragAreaClass: string = 'dragarea';
  showAlert = false;
  alertInfo = { title: '', message: '' };
  totalFilesToUpload = 0;
  filesUploaded = 0;
  isUploading = false;

  constructor(private chatService: ChatService) {}

  get isFileUploading(): boolean {
    return this.isUploading || (this.uploadedFiles.length > 0 && this.uploadedFiles.some((file) => file.uploading));
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (files.length > 0) {
      this.processFiles(files);
    }
  }

  closeAlert() {
    this.showAlert = false;
    this.alertInfo = { title: '', message: '' };
  }

  @HostListener('drop', ['$event']) onDrop(event: any) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer.files) {
      const files: FileList = event.dataTransfer.files;
      this.processFiles(files);
    }
  }

  private validateFileSize(file: File) {
    if (
      file.type.startsWith('image/') &&
      file.size > MAX_IMAGE_SIZE * 1024 * 1024
    ) {
      this.alertInfo = {
        title: 'File Error',
        message: `The attachment(s) size exceed the limit ${MAX_IMAGE_SIZE}MB.`,
      };
      this.showAlert = true;
      return false;
    }

    if (
      file.type.startsWith('video/') &&
      file.size > MAX_VIDEO_SIZE * 1024 * 1024
    ) {
      this.alertInfo = {
        title: 'File Error',
        message: `The attachment(s) size exceed the limit ${MAX_VIDEO_SIZE}MB.`,
      };
      this.showAlert = true;
      return false;
    }

    return true;
  }

  private async readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private async processFiles(files: FileList): Promise<void> {
    if (this.isUploading) return;
    this.isUploading = true;

    const newFiles = Array.from(files);

    if (this.uploadedFiles.length + newFiles.length > MAX_FILE_COUNT) {
      this.alertInfo = {
        title: 'File Error',
        message: `You can upload up to ${MAX_FILE_COUNT} files only.`,
      };
      this.showAlert = true;
      this.isUploading = false;
      return;
    }

    const fileLength = this.uploadedFiles.length;
    let id = fileLength > 0 ? this.uploadedFiles[fileLength - 1].id + 1 : 1;

    let convertedImagesCount = 0;
    const totalFiles = newFiles.length;

    this.totalFilesToUpload = newFiles.length;
    this.filesUploaded = 0;

    const validFiles: File[] = [];
    for (const file of newFiles) {
      if (!this.validateFileSize(file)) {
        this.totalFilesToUpload--;
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      this.isUploading = false;
      return;
    }

    const conversionPromises = validFiles.map(file => {
      if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
        convertedImagesCount++;
        return this.convertHeicToJpeg(file);
      } else if (file.type.startsWith('image/') && 
                (file.type !== 'image/jpeg' || 
                 file.name.toLowerCase().endsWith('.jfif'))) {
        convertedImagesCount++;
        return this.convertToJPEG(file);
      } else {
        return Promise.resolve(file);
      }
    });

    let convertedFiles: File[];
    try {
      convertedFiles = await Promise.all(conversionPromises);
    } catch (error) {
      console.error('Error during conversion:', error);
      this.alertInfo = {
        title: 'Conversion Error',
        message: 'Some files could not be converted. Please try again.',
      };
      this.showAlert = true;
      this.isUploading = false;
      return;
    }

    let successfulUploads = 0;
    for (let i = 0; i < convertedFiles.length; i++) {
      const file = convertedFiles[i];
      const currentFileId = id + i;

      try {
        const base64 = await this.readFileAsDataURL(file);

        const formattedFile: IRequestFile = {
          id: currentFileId,
          file_encode_base64: base64,
          type: file.type,
          size: file.size,
          name: file.name,
        };

        const uploadingFile = {
          id: currentFileId,
          file_display: '',
          type: file.type,
          size: file.size,
          name: file.name,
          uploading: true,
        };

        this.uploadedFiles.push(uploadingFile);

        const response = await this.chatService.uploadAttachments([formattedFile]).toPromise();

        const uploadedFileData = response.files_uploaded[0];
        const updatedFile = {
          id: currentFileId,
          file_display: uploadedFileData.external_long_url,
          type: uploadedFileData.type,
          size: uploadedFileData.size || uploadedFileData.image_file?.size || uploadedFileData.video_file?.size || 0,
          name: uploadedFileData.name || uploadedFileData.image_file?.name || uploadedFileData.video_file?.name || 'Unknown',
          uploading: false,
        };

        const index = this.uploadedFiles.findIndex(f => f.id === currentFileId);
        if (index !== -1) {
          this.uploadedFiles[index] = updatedFile;
        }

        successfulUploads++;
        this.filesUploaded = successfulUploads;

      } catch (error) {
        console.error('Error uploading file:', error);
        
        const index = this.uploadedFiles.findIndex(f => f.id === currentFileId);
        if (index !== -1) {
          this.uploadedFiles.splice(index, 1);
        }
        
        this.filesUploaded = i + 1;
      }
    }

    this.showUploadSuccessMessage(convertedFiles.length, convertedImagesCount, successfulUploads);
    this.showPreview = true;
    this.isUploading = false;
  }

  private showUploadSuccessMessage(totalFiles: number, convertedImagesCount: number, successCount?: number): void {
    const successfulUploads = successCount !== undefined ? successCount : totalFiles;
    let message = '';
    
    if (successfulUploads === 0) {
      message = 'No files were uploaded successfully.';
    } else if (successfulUploads === totalFiles) {
      if (totalFiles === 1) {
        message = convertedImagesCount === 1 
          ? 'Your image has been converted to JPEG for compatibility.'
          : 'File uploaded successfully.';
      } else {
        message = convertedImagesCount > 0
          ? convertedImagesCount === 1 
            ? 'Your image has been converted to JPEG for compatibility.'
            : 'Your images have been converted to JPEG for compatibility.'
          : `All ${totalFiles} files uploaded successfully.`;
      }
    } else {
      message = `${successfulUploads} out of ${totalFiles} files uploaded successfully.`;
      if (convertedImagesCount > 0) {
        message += ` ${convertedImagesCount} image(s) were converted to JPEG.`;
      }
    }

    if (message) {
      this.alertInfo = {
        title: successfulUploads === totalFiles ? 'Upload Complete' : 'Partial Upload',
        message: message
      };
      this.showAlert = true;
    }
  }

  private async convertToJPEG(file: File): Promise<File> {
    return new Promise<File>((resolve) => {
      if (!file.type.startsWith('image/')) {
        return resolve(file);
      }

      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const baseName = file.name.split('.')[0];
              const jpegFile = new File([blob], `${baseName}.jpeg`, {
                type: 'image/jpeg',
              });
              resolve(jpegFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          1
        );
      };

      reader.readAsDataURL(file);
    });
  }

  private async convertHeicToJpeg(file: File): Promise<File> {
    try {
      const jpegBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9
      }) as Blob;
      
      const jpegFile = new File([jpegBlob], `${file.name.split('.')[0]}.jpeg`, {
        type: 'image/jpeg',
      });
      
      return jpegFile;
    } catch (error) {
      console.error('HEIC conversion error:', error);
      throw error;
    }
  }

  RemoveFile(file: IResponseFile): void {
    if (!file) return;

    this.chatService.deleteAttachment(file.file_display).subscribe((_) => {});

    const index = this.uploadedFiles.findIndex(
      (uploaded) => uploaded.file_display === file.file_display
    );
    if (index !== -1) {
      this.uploadedFiles.splice(index, 1);
    }
    this.showPreview = this.uploadedFiles.length > 0;
  }

  confirmUpload(): void {
    const files = this.uploadedFiles.filter((q) => q.file_display);
    if (files.length === 0) return;

    this.chatService
      .send('Uploading attachments...', '', files)
      .subscribe((_) => {});

    this.showPreview = false;
  }

  cancelOperation(): void {
    if (this.uploadedFiles.length > 0) {
      this.uploadedFiles.forEach((file) =>
        this.chatService.deleteAttachment(file.file_display).subscribe({})
      );

      this.chatService.uploadedFiles = [];
    }
    this.chatService.send('Cancel adding attachments').subscribe((_) => {});

    this.uploadedFiles = [];
    this.showPreview = false;
  }

  @HostListener('dragover', ['$event']) onDragOver(event: any) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }

  @HostListener('dragenter', ['$event']) onDragEnter(event: any) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }

  @HostListener('dragend', ['$event']) onDragEnd(event: any) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }

  @HostListener('dragleave', ['$event']) onDragLeave(event: any) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }

  playVideo(file: IResponseFile) {
    const videoEl = document.querySelector(
      `video[src="${file.file_display}"]`
    ) as HTMLVideoElement | null;

    if (videoEl) {
      videoEl.play();
    }
  }

  fullscreenVideo(file: IResponseFile) {
    const videoEl = document.querySelector(
      `video[src="${file.file_display}"]`
    ) as HTMLVideoElement | null;

    if (videoEl && videoEl.requestFullscreen) {
      videoEl.requestFullscreen();
    }
  }

  downloadFile(file: IResponseFile) {
    const link = document.createElement('a');
    link.href = file.file_display;
    link.download = file.name || 'video';
    link.click();
  }

  changePlaybackSpeed(file: IResponseFile) {
    const videoEl = document.querySelector(
      `video[src="${file.file_display}"]`
    ) as HTMLVideoElement | null;

    if (videoEl) {
      videoEl.playbackRate = videoEl.playbackRate === 1 ? 1.5 : 1;
    }
  }

  pictureInPicture(file: IResponseFile) {
    const videoEl = document.querySelector(
      `video[src="${file.file_display}"]`
    ) as HTMLVideoElement | null;

    if (videoEl && (videoEl as any).requestPictureInPicture) {
      (videoEl as any).requestPictureInPicture();
    }
  }

  ngOnInit(): void {
    this.dragAreaClass = 'dragarea';
  }
}